import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { ApAnalyticsService, ClickAnalyticsData, FormStepAnalyticsData, PersistData } from '@ap/analytics';
import { ApContentService } from '@ap/content';
import { MarketingPersonalisatieResource } from '@ap/interface-apix-marketing-personalisatie';
import { UserCorrelation } from '@ap/interface-apix-marketing-personalisatie/';
import { ApLoggingService } from '@ap/logging';
import { ApPersistenceService } from '@ap/persistence';

import content from './mijn-login-analytics.config.json';
import { ANALYTICS_ID_PERSIST_DURATION } from './mijn-login-analytics.constants';

@Injectable({ providedIn: 'root' })
export class MijnLoginAnalyticsService {
    // Used to save the Analytics ID in memory for about a year.

    public constructor(
        private readonly analyticsService: ApAnalyticsService,
        private readonly contentService: ApContentService,
        private readonly logger: ApLoggingService,
        private readonly persistenceService: ApPersistenceService,
        private readonly marketingPersonalisatieResource: MarketingPersonalisatieResource
    ) {
        this.contentService.addContentItems(content);
    }

    public pushSignFormSelection(formStepName: string): void {
        const isRememberTokenChecked: boolean = this.persistenceService.getLocal('ap-sign', 'token') !== undefined;
        this.analyticsService.push(
            new FormStepAnalyticsData('Inloggen', 'angular', formStepName, 1, 'start', [
                { name: 'Onthoud gegevens', value: isRememberTokenChecked }
            ])
        );
    }

    public pushSignFormClick(): void {
        this.analyticsService.push(new ClickAnalyticsData('Link', 'Andere login', 'Inloggen'));
    }

    public pushClickEvent(clickedElement: string, clickAction: string): void {
        this.analyticsService.push(new ClickAnalyticsData(clickedElement, clickAction));
    }

    public pushPageView(): void {
        this.analyticsService.pushPageview(
            this.contentService.get('mijn.login.analytics.page.type'),
            this.contentService.get('mijn.login.analytics.product.name'),
            this.contentService.get('mijn.login.analytics.product.category'),
            this.contentService.get('mijn.login.analytics.page.site.segment'),
            this.contentService.get('mijn.login.analytics.page.site.section'),
            this.contentService.get('mijn.login.analytics.platform.name'),
            ['pageview', 'checkout']
        );
    }

    public sendAnalyticsId(): Observable<void> {
        return this.marketingPersonalisatieResource.getUserCorrelation().pipe(
            tap((userCorrelation: UserCorrelation) => {
                this.pushUsercustomerId(userCorrelation.cg_id);
            }),
            catchError((error) => {
                this.logger.error('Error getting UserCorrelation', error);
                return of(error);
            })
        );
    }

    private pushUsercustomerId(id: string): void {
        const analyticsIdInfo: PersistData = new PersistData(
            'PERSIST DATA',
            { userCustomerId: id },
            '.*',
            ANALYTICS_ID_PERSIST_DURATION,
            false
        );
        this.analyticsService.push(analyticsIdInfo);
    }
}
